/**
 * №22.3 (D-CH0/D-CH1) + №22.3.1 (D-AF1–5) — the docked collaboration panel.
 *
 * The third grid column of the App Shell, present on every `/app` surface, mounted
 * ONCE and surviving all navigation (D-CH3 — the panel never remounts). It now
 * renders both the ANCHOR and the ACTOR truthfully:
 *
 *  - the chip is two-part — **@ {anchor} · with {actor}** — where `{anchor}` is the
 *    resolved node's name (never "Desk", D-AF1) and `{actor}` is named before you
 *    speak (D-AF4): a hire's desk → that hire; a matter → Kuvio, never a hire
 *    uninvited. The composer carries the same to-line.
 *  - it FOLLOWS navigation by default (stream + chip + actor re-anchor together),
 *    with a pin (`ti-pin`) that holds a conversation, and an auto-pin while a run
 *    streams or the composer holds text (D-AF2); "return to @X" shows where follow went.
 *  - a HISTORY layer (`ti-history`) lists the anchor's conversations, resumable, no
 *    composer (D-AF3 / P1). A card answer opens the thread with a transition row (D-AF5).
 *  - it owns its composer (`StreamComposer`), and since S3 (§3 ②) that is the ONLY one:
 *    22.3's bottom bar was the initiator while the panel was collapsed, and it is gone.
 *
 * All glyphs are Tabler line-icons (`ti ti-*`), matching the chrome's icon family —
 * never emoji. Token-only, scoped to `.appshell` — no new CSS system (R1).
 */
import { useSearchParams } from "react-router";
import { useEffect, useRef, useState } from "react";
import { getCurrentWorkspaceId } from "../../../lib/auth";
import { useV3ChatRuntime } from "../../hooks/use-v3-chat-runtime";
import { useTaskDetail } from "../../hooks/use-task-detail";
import { useAnchorStream } from "../../hooks/use-anchor-stream";
import { useAppScope } from "../../hooks/use-app-scope";
import { actorAvatar } from "../../routes/app/app-scope";
import { AgentThread } from "../chat/AgentThread";
import { RunThreadSection, type ThreadLens } from "../task-detail/RunThreadSection";
import { PanelHistory } from "./PanelHistory";
import { useCoPanel, anchorKeyOf, type PanelTaskFocus } from "./co-panel-context";
import type { StreamActor, StreamEvent } from "../../../lib/types";
import { conversationIsSubject, type SubjectKind } from "../../routes/app/route-anchor-matrix";

/**
 * A stable marker so a CI navigation-sequence test can prove the panel is mounted
 * once and never remounts (D-CH3). Set on mount; it changes only if the component
 * remounts OR the document fully reloads (a fresh `window`).
 */
function useMountMarker() {
  useEffect(() => {
    const w = window as unknown as { __COPANEL_MOUNT_ID__?: string; __COPANEL_MOUNTS__?: number };
    w.__COPANEL_MOUNT_ID__ = `copanel_${Math.random().toString(36).slice(2)}_${performance.now().toFixed(0)}`;
    w.__COPANEL_MOUNTS__ = (w.__COPANEL_MOUNTS__ ?? 0) + 1;
  }, []);
}

/** The "· with {actor}" chip (D-AF4). The un-personified workspace chat is the dark
 *  "K" mark; a named hire takes the accent ramp. The pin indicator is a sibling. */
function ActorWith({ actor }: { actor: StreamActor }) {
  const av = actorAvatar(actor);
  return (
    <span className="copanel__with" data-testid="copanel-actor">
      · with
      <span className={`copanel__avm ${av.workspace ? "is-workspace" : "is-hire"}`} aria-hidden="true">{av.initials}</span>
      <b>{actor.name}</b>
    </span>
  );
}

/**
 * №29 (§3 ①, S3) — this is the conversation BODY, and only that. It mounts inside
 * the WORK RAIL's Chat tab (or, promoted, the chat subject's stage) through the
 * conversation host's portal; it is never the shell's own third column.
 *
 * S0 re-housed it behind an `embedded` flag and left the old docked frame standing
 * on the other branch. S3 deletes that branch: the rail owns collapse (its edge tab
 * and its collapse control are the only ones), and the element is a plain block —
 * the rail is the landmark. Every 22.x contract inside — anchor/actor chip,
 * follow/pin, history, the empty conversation, the answer choreography — is
 * untouched, which is the whole point: D-SS2 re-houses them, it does not re-decide
 * them. What died is the FRAME, never the contracts.
 */
export function CoPanel() {
  const panel = useCoPanel();
  useMountMarker();
  const { ctx, anchor, chip, actor, subject } = useAppScope();

  // FOLLOW (D-AF2) — the panel re-anchors when you move somewhere else, UNLESS it is
  // pinned (manual, or auto while a run streams / the composer holds text). The panel
  // never remounts (D-CH3); this is a state reset, not a route change.
  //
  // №29 (S1) — it keys off the ANCHOR, not the pathname. It used to key off the
  // pathname, which was the same thing right up until the chat subject arrived: a
  // conversation promoted to the stage lives at a different URL with the SAME anchor
  // (that is the whole point — see `conversationRow`), so a pathname-keyed follow
  // dropped the focused run at the exact moment the user asked to look at it more
  // closely. D-AF2's own words are "navigation re-anchors the panel"; this is that
  // rule stated in terms of the thing it is about. The visible consequence beyond the
  // chat subject: moving between two surfaces that share an anchor — a record and its
  // process view, the ledger's own sub-views — now HOLDS the conversation instead of
  // resetting it, which is what a persistent layer (D-CH1) should do.
  const anchorKey = `${anchor.kind}:${anchor.id ?? ""}`;
  const prevAnchor = useRef(anchorKey);
  useEffect(() => {
    if (prevAnchor.current === anchorKey) return;
    prevAnchor.current = anchorKey;
    if (panel.pinned) return;
    // №29 (S4.3) — FOLLOW re-anchors, and if you have talked at the anchor you just arrived
    // at, it re-opens THAT conversation instead of an empty one. Not a reopening of 22.3.2
    // F2b: F2b is about the panel speaking first (no rollup, no seeded receipts), and a
    // fresh anchor still opens empty. This is the panel remembering what YOU said, where
    // you said it — the absence of which read as "my chat vanished".
    if (!panel.resumeConversation(anchorKey)) panel.follow();
    // `panel` is a stable memoized context value; the effect keys off the anchor change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey, panel.pinned, panel.follow, panel.resumeConversation]);

  // The ONE anchor-stream subscription (D-CV11): identity (server-resolved name +
  // actor, D-AF1/D-AF4) AND events, passed down so nothing double-subscribes. Live
  // while expanded. Declared BEFORE the collapse early-return so the hook order is
  // stable across expand/collapse (Rules of Hooks).
  // №22.3.2 (F1) — the identity (server-resolved name + actor) now comes from
  // `useAppScope`, ABOVE both the bar and the panel, so the two can no longer render
  // different chips for the same route. This subscription is the LIVE events one.
  const stream = useAnchorStream(anchor, { live: panel.expanded });
  const events = stream.data?.events ?? [];

  const focused = panel.focusedTask;
  const away = !!focused || panel.chatActive;

  // The header's (anchor, actor):
  //  - a focused task names its own agent + anchor (frozen in focusedTask);
  //  - a scope chat uses its FROZEN snapshot (D-AF2 — a pinned chat holds its identity
  //    even as the route changes underneath, complaint ②);
  //  - the default stream uses the SERVER-resolved anchor name + actor (D-AF1),
  //    falling back to the route-resolved values before the stream loads.
  const snap = panel.chatSnapshot;
  // The name of the anchor FOLLOW is tracking (the current route) — used by the
  // return-to-here strip and the history "new chat" row so they name one thing.
  const routeAnchorName = chip;
  const headerAnchor = focused
    ? (focused.label ?? focused.agentName ?? "Task")
    : (panel.chatActive && snap ? snap.chip : routeAnchorName);
  const headerActor: StreamActor = focused
    // D-L6 (№22.5 ride-along) — the fallback DISPLAY NAME when a hire's name has not
    // loaded. "Agent" named the model; "the hire" names the person, honestly, without
    // inventing a name we do not have.
    ? { kind: "agent", id: focused.agentId ?? null, name: focused.agentName ?? "the hire", is_workspace: false }
    : (panel.chatActive && snap ? snap.actor : actor);

  return (
    <div className="copanel" data-testid="copanel">
      <div className="copanel__h">
        {/* №29 (S2, Q3 → B) — WHO NAMES THE ANCHOR. When the conversation IS the
            subject, the stage head above it already names the anchor (and, when a
            run is focused, that run) — so naming it again here is the same word
            twice on one screen. The chip is therefore dropped on a chat subject and
            kept everywhere else, where the conversation sits in the rail with no
            head of its own. What is NOT dropped is "· with {hire}": D-AF4 says the
            actor is named before you speak, and that contract is untouched. Drawn
            both ways at the S1 STOP — docs/29-shell-options/Q3-anchor-on-stage.png. */}
        {/* THE ANCHOR STAYS — ruled by David, after the ask-scoping decision:
            *"fine to keep — I'd like to think the 'node' is always relevant."*
            Recorded here because the rule that produced this panel's new shape
            ("scope to the ASK, not its container") would otherwise argue this chip
            away, and a later lane would be right to strip it on those grounds. It is
            the ONE piece of container context that is always earned: an ask with no
            node is a question about nothing, and D-AF1/D-AF2 already make this the
            frozen identity of the conversation. */}
        {!conversationIsSubject(subject) && (
          <span className="ctxchip" title={headerAnchor} data-testid="copanel-anchor">@ {headerAnchor}</span>
        )}
        {!panel.historyOpen && <ActorWith actor={headerActor} />}
        {/* the pin is a conversation-state indicator on the "with" chip (P1), in a fixed
            slot so a long anchor never clips it. */}
        {!panel.historyOpen && panel.manualPinned && (
          <i className="ti ti-pin copanel__pin" title="pinned — navigation won't switch this chat" data-testid="copanel-pinned" aria-hidden="true" />
        )}
        {panel.historyOpen && <span className="copanel__with"><i className="ti ti-history" aria-hidden="true" /> <b>History</b></span>}
        <span className="spacer" />
        {/* history (D-AF3 / P1) — toggles the list layer; the pin is a state indicator
            on the "with" chip, NOT a history control. */}
        <button
          type="button"
          className={`copanel__icon ${panel.historyOpen ? "is-on" : ""}`}
          onClick={panel.toggleHistory}
          aria-label={panel.historyOpen ? "Close history" : "History"}
          aria-pressed={panel.historyOpen}
          title={panel.historyOpen ? "Close history" : "History"}
          data-testid="copanel-history"
        >
          <i className={panel.historyOpen ? "ti ti-x" : "ti ti-history"} aria-hidden="true" />
        </button>
        {/* №29 (§3 ①) — no collapse control here. The RAIL owns collapse; a second
            button for one state is the duplication this removal exists to end. */}
      </div>

      {/* the manual-pin toggle + return-to-here strip (D-AF2). Shown when away from the
          route's default stream. Auto-pin (streaming) reads "return to here"; a manual
          pin reads "pinned". */}
      {away && (
        <div className="copanel__ret" data-testid="copanel-ret">
          <span>
            {panel.manualPinned ? (
              <><i className="ti ti-pin" aria-hidden="true" /> pinned — navigation won't switch this chat</>
            ) : panel.pinned ? (
              <><i className="ti ti-player-play" aria-hidden="true" /> auto-pinned while this runs</>
            ) : (
              "following — this switches as you navigate"
            )}
          </span>
          <button type="button" className="copanel__retlink" onClick={panel.follow} data-testid="copanel-here">
            return to @{routeAnchorName} <i className="ti ti-corner-down-left" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`copanel__pinbtn ${panel.manualPinned ? "is-on" : ""}`}
            onClick={panel.togglePin}
            aria-pressed={panel.manualPinned}
            title={panel.manualPinned ? "Unpin (follow navigation)" : "Pin this conversation"}
            data-testid="copanel-pin"
          >
            <i className="ti ti-pin" aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="copanel__body" data-testid="copanel-body">
        {panel.historyOpen ? (
          <PanelHistory events={events} loading={stream.isLoading} chip={routeAnchorName} onResume={panel.focusTask} onNewChat={panel.showStream} />
        ) : focused ? (
          <TaskStream focus={focused} subject={subject} />
        ) : panel.chatActive && snap ? (
          <ScopeChat ctx={snap.ctx} anchor={snap.anchor} actor={snap.actor} />
        ) : (
          <EmptyScope chip={routeAnchorName} subject={subject} events={events} onOpenHistory={panel.toggleHistory} />
        )}
      </div>

      {/* the composer for the default stream view — the only one, since §3 ② deleted
          the bottom bar (D-CH1). The task/scope modes bring
          their own composers (RunThreadSection / AgentThread); history has none (P1). */}
      {!panel.historyOpen && !focused && !panel.chatActive && (
        <StreamComposer
          actor={headerActor}
          placeholder={ctx.placeholder}
          onSend={(text) => panel.postToScope(text, { ctx, anchor, chip: routeAnchorName, actor: headerActor })}
        />
      )}
    </div>
  );
}

/**
 * №22.3.2 (F2b) — the panel's DEFAULT body: an EMPTY conversation, plus one quiet line
 * pointing at the history layer. David's ruling, after the first attempt.
 *
 * 22.3 put the anchor's rollup here — runs, open cards, activity — directly above the
 * composer. Dressing it as a list (22.3.2's first pass) was not enough: a receipt
 * carrying a whole agent reply, stacked over a composer, still reads as a chat thread
 * that spoke to you first. So the rollup is gone from this layer entirely. It lives in
 * the history layer, which is what it always was, and this line is the only thing left
 * of it: a count and a way in.
 *
 * The chat window is therefore empty until you type. That is the doctrine, literally:
 * the panel speaks only when spoken to.
 */
function EmptyScope({
  chip,
  subject,
  events,
  onOpenHistory,
}: {
  chip: string;
  /** №29 (S2) — the INTAKE subject gets its own sentence. See below. */
  subject: SubjectKind;
  events: StreamEvent[];
  onOpenHistory: () => void;
}) {
  const runs = events.filter((e) => e.kind === "run").length;
  const cards = events.filter((e) => e.kind === "card").length;
  const parts = [
    runs > 0 ? `${runs} run${runs === 1 ? "" : "s"}` : null,
    cards > 0 ? `${cards} need${cards === 1 ? "s" : ""} you` : null,
  ].filter(Boolean);

  return (
    <div className="copanel__scope" data-layer="conversation" data-testid="copanel-scope-empty">
      {parts.length > 0 && (
        <button type="button" className="copanel__glance" onClick={onOpenHistory} data-testid="copanel-glance">
          <span>{parts.join(" · ")}</span>
          <span className="copanel__glancelink">see history <i className="ti ti-arrow-right" aria-hidden="true" /></span>
        </button>
      )}
      <div className="copanel__empty">
        {/* №29 (S2) — INTAKE has no rollup to promise, and promising one is the
            failure this whole layer is built against. There is no "New" node with
            tasks, dates, documents and memories: nothing exists yet — that is what
            intake IS. So the sentence says what this conversation can actually do,
            and the anchor sentence is kept for the anchors that have an anchor. */}
        {subject === "intake" ? (
          <>
            Describe what you need and it gets opened here — a matter, a contact, a piece of work.
            Nothing exists yet, so there is nothing to roll up.
          </>
        ) : (
          <>
            Start here — I can see everything under <b>{chip}</b>: its tasks, dates, documents, and
            memories. Past runs and open decisions are in history.
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The composer that sits under the empty scope (D-CH1) — a to-line naming the actor
 * (D-AF4) + an input. Since S3 deleted the bottom bar (§3 ②) this is THE composer for
 * a scope chat, not the one that takes over from it. Committing STARTS a conversation,
 * which takes over the pane with the reply. №22.3.2 (F2): it says so ("start a conversation at @X"), because
 * the thing above it is a list you read, not a thread you're already in — and the
 * conversation it starts contains exactly one turn: the one you just typed. The
 * placeholder is the surface's own, straight off its route→anchor matrix row.
 */
function StreamComposer({
  actor,
  placeholder,
  onSend,
}: {
  actor: StreamActor;
  placeholder: string;
  onSend: (text: string) => void;
}) {
  const [draft, setDraft] = useState("");
  const av = actorAvatar(actor);
  const send = () => {
    const t = draft.trim();
    if (!t) return;
    onSend(t);
    setDraft("");
  };
  return (
    <div className="copanel__foot" data-layer="composer">
      <div className="copanel__to" data-testid="copanel-toline">
        to <span className={`copanel__avm ${av.workspace ? "is-workspace" : "is-hire"}`} aria-hidden="true">{av.initials}</span> <b>{actor.name}</b>
        {av.workspace ? <span className="copanel__toswitch"> · @ to address a hire</span> : null}
      </div>
      <div className="copanel__composer">
        <input
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          data-testid="copanel-composer-input"
        />
        <button type="button" className="copanel__send" onClick={send} disabled={!draft.trim()} title="Send" aria-label="Send">↑</button>
      </div>
    </div>
  );
}

/**
 * The focused task-run stream — the REAL cockpit thread, rendered docked (R1). Opens
 * with a transition row when entered via a card answer (D-AF5) and carries the actor
 * to-line above the composer (D-AF4). Reports streaming/composing up so the panel
 * auto-pins (D-AF2).
 */
function TaskStream({ focus, subject }: { focus: PanelTaskFocus; subject: SubjectKind }) {
  const panel = useCoPanel();
  const query = useTaskDetail(focus.taskId);
  /**
   * THE GESTURE SPLIT (ruled with David, after the №22.6 walk).
   *
   *   you CLICKED to look   → `card-first`: the ask, and nothing you have to reconcile
   *                           with it. Prior work is one self-describing line per run.
   *   you ANSWERED          → `full`: D-CH2's choreography is right — you want to watch
   *                           the run land, and the transcript IS the point.
   *
   * `viaAnswer` is stamped by `notifyAnswered`, so the two gestures are already
   * distinguishable and nothing new had to be threaded through to tell them apart.
   *
   * `?panel_lens=full` / `?panel_lens=card-first` forces either, which is how the
   * options were drawn (`npm run options:panel-entry`) and how a regression is
   * inspected. Read through react-router, never `window.location`: the SPA-nav gate
   * is right to refuse the latter even for a read, because the next edit turns a read
   * into a jump.
   */
  const [params] = useSearchParams();
  const forced = params.get("panel_lens");
  const lens: ThreadLens =
    forced === "card-first" ? "card-first"
      : forced === "full" ? "full"
        // D-NY5 scopes the DOCKED panel — a glance surface beside your work. When the
        // conversation has been PROMOTED to the stage it is the SUBJECT: promoting is
        // an explicit act meaning "I want the conversation", and there the record is
        // the point, not an aside to be dismissed. (This is also load-bearing: the
        // panel is portalled into the stage slot, so a card-first default there hid
        // the thread the D-CH2 answer choreography streams into.)
        : conversationIsSubject(subject) ? "full"
          : focus.viaAnswer ? "full" : "card-first";

  if (query.isLoading) {
    return <div className="copanel__loading"><div className="skeleton sk-line" style={{ width: "70%" }} /><div className="skeleton sk-line" style={{ width: "90%" }} /><div className="skeleton sk-line" style={{ width: "50%" }} /></div>;
  }
  if (query.error || !query.data) {
    return (
      <div className="copanel__empty">
        Couldn’t load this run. {(query.error as Error)?.message ?? "It may have been archived, or you may not have access."}
      </div>
    );
  }

  const { task } = query.data;
  const actorName = task.agent_name ?? focus.agentName ?? "the hire"; // D-L6
  const initials = actorName.slice(0, 2).toUpperCase();
  const transition = focus.viaAnswer ? (
    <div className="copanel__trans" data-testid="copanel-transition">
      now in: {task.title ?? focus.label ?? "this run"} · with <b>{actorName}</b>
    </div>
  ) : null;
  const toLine = (
    <div className="copanel__to" data-testid="copanel-toline">
      to <span className="copanel__avm is-hire" aria-hidden="true">{initials}</span> <b>{actorName}</b> · steering this run
    </div>
  );

  return (
    <div className="copanel__stream">
      <RunThreadSection
        /* Keyed by task: opening one ask's record must not pre-open the next one you
           click. The lens is a starting state, and this is what keeps it starting. */
        key={focus.taskId}
        /* PROPOSED (default OFF) — see ThreadLens. `?panel_lens=card-first` renders
           the alternative so the two can be compared from the SHIPPED build rather
           than from a description of them. Answering is unaffected either way: the
           D-CH2 choreography (watch the run land) still opens the full stream. */
        lens={lens}
        taskId={focus.taskId}
        agentId={task.agent_id ?? focus.agentId ?? ""}
        agentName={actorName}
        task={task}
        transition={transition}
        toLine={toLine}
        onBusyChange={panel.setBusy}
      />
    </div>
  );
}

/**
 * The route-scoped "Here" chat — what a composer posts into (D-CH1). The ACTOR is
 * server-enforced (D-AF4): an agent-desk anchor routes to that hire; every other
 * anchor (matter / workflow / workspace) routes to `/ai/chat` → the workspace root
 * ("Kuvio"), so a fresh anchored chat NEVER resolves to a hire uninvited.
 */
function ScopeChat({ ctx, anchor, actor }: { ctx: ReturnType<typeof useAppScope>["ctx"]; anchor: ReturnType<typeof useAppScope>["anchor"]; actor: StreamActor }) {
  const panel = useCoPanel();
  // Only an agent-desk anchor addresses a hire directly; everything else = Kuvio.
  const agentNodeId = anchor.kind === "agent" ? anchor.id : undefined;
  // №29 (S4.3) — the id comes from the panel's memory of this anchor, minted once when the
  // conversation started. It used to be `crypto.randomUUID()` per MOUNT, which orphaned the
  // conversation on every navigation: it lived on the server and no id existed to reach it.
  const remembered = panel.recallConversation(anchorKeyOf(anchor));
  const conversationId = remembered?.id ?? `${anchor.kind}:${anchor.id ?? ""}:pending`;

  const { runtime, chatMessages, status, error, clearError, regenerate } = useV3ChatRuntime({
    conversationId,
    workspaceId: getCurrentWorkspaceId() ?? undefined,
    agentNodeId,
    scope: ctx.scope,
    pageContext: ctx.page,
  });

  // Composer hand-off (D-CH1): when a composer posts (postNonce bumps), append here.
  const lastPost = useRef(0);
  useEffect(() => {
    if (panel.postNonce === lastPost.current) return;
    lastPost.current = panel.postNonce;
    const text = (panel.draft ?? "").trim();
    if (!text) return;
    runtime.thread.append({ role: "user", content: [{ type: "text", text }] });
    panel.setDraft(null);
    if (error) clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel.postNonce]);

  const av = actorAvatar(actor);
  const emptyState = (
    <div className="copanel__empty">
      Start here — I can see everything under <b>{ctx.chip}</b>: its tasks, dates, documents, and memories. Or click a card to watch its run here.
    </div>
  );
  const toLine = (
    <div className="copanel__to" data-testid="copanel-toline">
      to <span className={`copanel__avm ${av.workspace ? "is-workspace" : "is-hire"}`} aria-hidden="true">{av.initials}</span> <b>{actor.name}</b>
      {av.workspace ? <span className="copanel__toswitch"> · @ to address a hire</span> : null}
    </div>
  );

  return (
    // F2 — the CONVERSATION layer, distinct from the list layer above it. It opens
    // EMPTY at every anchor: the only turns in it are the ones the user typed and the
    // replies to them. A stream row or a Needs-You card is never seeded in here.
    <div className="copanel__chat" data-layer="conversation" data-testid="copanel-conversation">
      <AgentThread
        runtime={runtime}
        chatMessages={chatMessages}
        chatStatus={status}
        chatError={error}
        emptyState={emptyState}
        assistantLabel={actor.name ?? "Kuvio"}
        toLine={toLine}
        onRetry={regenerate}
        onOAuthContinue={regenerate}
      />
    </div>
  );
}
